<template>
  <div class="admin-layout">
    <!-- Sidebar -->
    <aside class="sidebar">
      <div class="sidebar-logo">
        <h2>Mentor Spark</h2>
        <span>{{ $t('bo.layout.backoffice') }}</span>
      </div>

      <nav class="sidebar-nav">
        <div v-for="section in menuSections" :key="section.titleKey" class="nav-section">
          <div class="nav-section-title">{{ $t(section.titleKey) }}</div>
          <router-link
            v-for="item in section.items"
            :key="item.route"
            :to="item.route"
            class="nav-item"
            active-class="nav-item-active"
          >
            <span class="nav-icon">{{ item.icon }}</span>
            {{ $t(item.labelKey) }}
          </router-link>
        </div>
      </nav>

      <div class="sidebar-footer">
        <div class="user-info">
          <div class="user-avatar">{{ userInitials }}</div>
          <div class="user-details">
            <div class="user-name">{{ authStore.user?.name || 'Admin' }}</div>
            <div class="user-email">{{ authStore.user?.email }}</div>
          </div>
        </div>
        <div class="lang-switcher">
          <button :class="['lang-btn', { active: currentLocale === 'fr' }]" @click="switchLocale('fr')">FR</button>
          <button :class="['lang-btn', { active: currentLocale === 'en' }]" @click="switchLocale('en')">EN</button>
        </div>
        <button @click="handleLogout" class="btn-logout">{{ $t('bo.layout.signOut') }}</button>
      </div>
    </aside>

    <!-- Main content -->
    <main class="main-content">
      <router-view />
    </main>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { currentLocale, setLocale } from '@/i18n'

const router = useRouter()
const authStore = useAuthStore()

const switchLocale = (lang) => setLocale(lang)

const menuSections = [
  {
    titleKey: 'bo.layout.sections.platform',
    items: [
      { labelKey: 'bo.layout.menu.dashboard',      icon: '🏠', route: '/admin/dashboard' },
      { labelKey: 'bo.layout.menu.organizations',  icon: '🏢', route: '/admin/organizations' },
      { labelKey: 'bo.layout.menu.users',          icon: '👥', route: '/admin/users' },
    ]
  },
  {
    titleKey: 'bo.layout.sections.content',
    items: [
      { labelKey: 'bo.layout.menu.translations',      icon: '🌐', route: '/admin/translations' },
      { labelKey: 'bo.layout.menu.sessionTemplates',  icon: '📋', route: '/admin/session-templates' },
      { labelKey: 'bo.layout.menu.emailTemplates',    icon: '✉️', route: '/admin/email-templates' },
      { labelKey: 'bo.layout.menu.resources',         icon: '📚', route: '/admin/resources' },
      { labelKey: 'bo.layout.menu.trainings',         icon: '🎓', route: '/admin/trainings' },
      { labelKey: 'bo.layout.menu.programs',          icon: '🧭', route: '/admin/programs' },
      { labelKey: 'bo.layout.menu.escoData',          icon: '🗂️', route: '/admin/esco' },
      { labelKey: 'bo.layout.menu.platformSkills',    icon: '🧩', route: '/admin/platform-skills' },
      { labelKey: 'bo.layout.menu.occupationSkills',  icon: '💼', route: '/admin/occupation-skills' },
      { labelKey: 'bo.layout.menu.aiPrompts',          icon: '🤖', route: '/admin/ai-prompts' },
    ]
  },
  {
    titleKey: 'bo.layout.sections.billing',
    items: [
      { labelKey: 'bo.layout.menu.billing', icon: '💳', route: '/admin/billing' },
      { labelKey: 'bo.layout.menu.catalog', icon: '🏷️', route: '/admin/catalog' },
    ]
  },
  {
    titleKey: 'bo.layout.sections.monitoring',
    items: [
      { labelKey: 'bo.layout.menu.notifications', icon: '🔔', route: '/admin/notifications' },
    ]
  },
  {
    titleKey: 'bo.layout.sections.settings',
    items: [
      { labelKey: 'bo.layout.menu.settings', icon: '⚙️', route: '/admin/settings' },
    ]
  }
]

const userInitials = computed(() => {
  const name = authStore.user?.name || authStore.user?.email || 'A'
  return name.charAt(0).toUpperCase()
})

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}
</script>

<style scoped>
.admin-layout {
  display: flex;
  min-height: 100vh;
  background: #f9fafb;
}

/* ── Sidebar ── */
.sidebar {
  width: 260px;
  min-width: 260px;
  background: #1e293b;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  overflow-y: auto;
  z-index: 100;
}

.sidebar-logo {
  padding: 1.5rem;
  border-bottom: 1px solid #334155;
}

.sidebar-logo h2 {
  margin: 0 0 0.25rem;
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
}

.sidebar-logo span {
  color: #94a3b8;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.sidebar-nav {
  flex: 1;
  padding: 1rem 0;
}

.nav-section {
  margin-bottom: 1.5rem;
}

.nav-section-title {
  padding: 0 1.25rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #64748b;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1.25rem;
  color: #94a3b8;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.15s;
  border-left: 3px solid transparent;
}

.nav-item:hover {
  background: #334155;
  color: white;
}

.nav-item-active {
  background: #334155;
  color: white;
  border-left-color: #3b82f6;
}

.nav-icon {
  font-size: 1rem;
  width: 1.25rem;
  text-align: center;
}

.sidebar-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid #334155;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.user-avatar {
  width: 2rem;
  height: 2rem;
  background: #3b82f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.user-details {
  overflow: hidden;
}

.user-name {
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-email {
  color: #64748b;
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lang-switcher {
  display: flex;
  gap: 0.375rem;
  margin-bottom: 0.625rem;
}

.lang-btn {
  flex: 1;
  padding: 0.35rem 0.5rem;
  background: transparent;
  border: 1px solid #334155;
  border-radius: 0.25rem;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.05em;
  transition: all 0.15s;
}

.lang-btn:hover {
  background: #334155;
  color: #94a3b8;
}

.lang-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.btn-logout {
  width: 100%;
  padding: 0.5rem;
  background: transparent;
  border: 1px solid #334155;
  border-radius: 0.375rem;
  color: #94a3b8;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-logout:hover {
  background: #334155;
  color: white;
}

/* ── Main content ── */
.main-content {
  margin-left: 260px;
  flex: 1;
  min-height: 100vh;
  overflow-x: hidden;
}
</style>
